import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Send, CalendarDays, Clock, UtensilsCrossed, Sigma, User, Building2, ShieldCheck } from 'lucide-react';
import { differenceInCalendarDays, format } from 'date-fns';
import { es } from 'date-fns/locale';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthGate from '../components/AuthGate';
import { PageHeader } from '../components/SectionHeader';
import { Calendar } from '../components/ui/calendar';
import { CATEGORIES, PER_DIEM_LABELS } from '../data/demo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOB_TYPES = [
  { id: 'trabajo', label: 'Trabajo' },
  { id: 'sustitucion', label: 'Sustitución' },
  { id: 'refuerzo', label: 'Refuerzo' },
  { id: 'bolsa', label: 'Bolsa de trabajo' },
  { id: 'urgente', label: 'Urgente' },
  { id: 'otro', label: 'Otro' },
];

const CAL_CLASSES = {
  caption_label: 'text-sm font-medium text-white',
  head_cell: 'text-slate-500 rounded-md w-9 font-normal text-[0.75rem] font-mono uppercase',
  nav_button: 'h-7 w-7 bg-transparent p-0 text-slate-300 border border-[#2A2F3D] hover:border-amber-500/60 hover:text-amber-500 rounded-md inline-flex items-center justify-center',
  day: 'h-9 w-9 p-0 font-normal text-slate-300 rounded-md hover:bg-[#1A1D24] hover:text-white aria-selected:opacity-100 inline-flex items-center justify-center text-sm',
  day_selected: 'bg-amber-500 text-slate-950 hover:bg-amber-500 hover:text-slate-950 focus:bg-amber-500 focus:text-slate-950 font-semibold',
  day_today: 'border border-amber-500/50 text-amber-400',
  day_outside: 'text-slate-700 aria-selected:bg-amber-500/20',
  day_range_middle: 'aria-selected:bg-amber-500/20 aria-selected:text-amber-200 rounded-none',
  day_range_start: 'rounded-r-none',
  day_range_end: 'rounded-l-none',
  cell: 'relative p-0 text-center focus-within:relative focus-within:z-20',
  day_disabled: 'text-slate-700 opacity-40',
};

const toMinutes = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };

const parseYmd = (ymd) => { if (!ymd) return undefined; const [y, m, d] = ymd.split('-').map(Number); return new Date(y, m - 1, d); };

export const computeHours = ({ durationType, singleDate, range, startTime, endTime }) => {
  if (!startTime || !endTime) return { days: 0, hoursPerDay: 0, total: 0 };
  let perDay = (toMinutes(endTime) - toMinutes(startTime)) / 60;
  if (perDay <= 0) perDay += 24; // overnight shift
  let days = 0;
  if (durationType === 'un-dia') days = singleDate ? 1 : 0;
  else if (range?.from) days = range.to ? differenceInCalendarDays(range.to, range.from) + 1 : 1;
  const total = Math.round(days * perDay * 4) / 4;
  return { days, hoursPerDay: Math.round(perDay * 4) / 4, total };
};

const Inner = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('edit');
  const [form, setForm] = useState({
    title: '', category: '', type: 'trabajo', location: '',
    compensation: '', skills: '', description: '', deadline: '',
    perDiem: 'none', perDiemDetail: '',
  });
  const [durationType, setDurationType] = useState('un-dia');
  const [singleDate, setSingleDate] = useState(undefined);
  const [range, setRange] = useState(undefined);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    fetch(`${API}/profile/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then(setProfile)
      .catch(() => setProfile(null));
  }, []);

  useEffect(() => {
    if (!editId) return;
    fetch(`${API}/jobs/${editId}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j) { toast.error('No se pudo cargar la oferta a editar'); return; }
        setForm({
          title: j.title || '', category: j.category || '', type: j.type || 'trabajo',
          location: j.location || '', compensation: j.compensation || '',
          skills: (j.skills || []).join(', '), description: j.description || '',
          deadline: j.deadline || '', perDiem: j.perDiem || 'none', perDiemDetail: j.perDiemDetail || '',
        });
        setDurationType(j.durationType || 'un-dia');
        if (j.startTime) setStartTime(j.startTime);
        if (j.endTime) setEndTime(j.endTime);
        if ((j.durationType || 'un-dia') === 'un-dia') {
          setSingleDate(parseYmd(j.startDate));
        } else {
          setRange({ from: parseYmd(j.startDate), to: parseYmd(j.endDate || j.startDate) });
        }
      })
      .catch(() => toast.error('No se pudo cargar la oferta a editar'));
  }, [editId]);

  const isCompany = profile?.account_type === 'company';
  const publisherName = isCompany ? (profile?.company_name || 'Tu empresa') : (profile?.nick || profile?.name || 'Tu perfil profesional');

  const calc = useMemo(() => computeHours({ durationType, singleDate, range, startTime, endTime }), [durationType, singleDate, range, startTime, endTime]);
  const fmtD = (d) => format(d, "d MMM yyyy", { locale: es });
  const scheduleLabel = useMemo(() => {
    if (durationType === 'un-dia') return singleDate ? `${fmtD(singleDate)} · ${startTime}–${endTime}` : '';
    if (!range?.from) return '';
    return `${fmtD(range.from)} → ${range.to ? fmtD(range.to) : fmtD(range.from)} · ${startTime}–${endTime} diario`;
  }, [durationType, singleDate, range, startTime, endTime]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.location || !form.description) {
      toast.error('Completa título, categoría, ubicación y descripción');
      return;
    }
    if (calc.days === 0) { toast.error('Selecciona la fecha (o el rango de fechas) en el calendario'); return; }
    setSaving(true);
    try {
      const startDate = durationType === 'un-dia' ? singleDate : range.from;
      const endDate = durationType === 'un-dia' ? singleDate : (range.to || range.from);
      const payload = {
        ...form,
        perDiemDetail: form.perDiemDetail || null,
        durationType,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        startTime, endTime,
        schedule: scheduleLabel,
        totalHours: calc.total,
        skills: form.skills.split(',').map((s) => s.trim()).filter(Boolean),
      };
      const res = await fetch(`${API}/jobs/${editId ? editId : 'drafts'}`, {
        method: editId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      if (editId) {
        toast.success('Oferta actualizada.');
        setTimeout(() => navigate(`/ofertas/${editId}`), 700);
      } else {
        toast.success('¡Oferta publicada! Ya está visible en el tablón.');
        setTimeout(() => navigate('/ofertas'), 800);
      }
    } catch {
      toast.error(editId ? 'No se pudo actualizar la oferta. Inténtalo de nuevo.' : 'No se pudo publicar la oferta. Inténtalo de nuevo.');
    } finally { setSaving(false); }
  };

  const inp = 'w-full bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-white text-sm';
  const lbl = 'block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2';
  const chip = (active) => `px-3 py-2 text-xs font-mono uppercase tracking-widest rounded-full border transition-colors ${active ? 'bg-amber-500 text-slate-950 border-amber-500' : 'text-slate-300 border-[#2A2F3D] hover:border-amber-500/40'}`;

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader title={editId ? 'Editar oferta' : 'Publicar una oferta'} subtitle={editId ? 'Modifica los datos de tu oferta y guarda los cambios. Se actualizará al instante en el tablón.' : 'Rellena los datos principales y el equipo Stage Crew revisará la oferta antes de publicarla.'} testId="publish-header" />
        {profile && (
          <div className={`sc-card p-4 mb-6 flex items-center gap-3 ${isCompany ? 'border-emerald-500/30' : 'border-amber-500/30'}`} data-testid="publish-as-banner">
            {isCompany ? <Building2 size={20} className="text-emerald-400 shrink-0" /> : <User size={20} className="text-amber-500 shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Publicarás como</div>
              <div className="text-sm text-white font-semibold flex items-center gap-2 flex-wrap">
                <span data-testid="publish-as-name">{publisherName}</span>
                {isCompany
                  ? (profile.company_verified
                    ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-mono"><ShieldCheck size={13} /> Empresa verificada</span>
                    : <span className="text-amber-400 text-xs font-mono">Empresa (sin verificar)</span>)
                  : <span className="text-amber-400 text-xs font-mono">Profesional</span>}
              </div>
            </div>
            <Link to="/mi-perfil" className="text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-amber-500 shrink-0">Cambiar →</Link>
          </div>
        )}
        {isCompany && !profile?.company_verified && (
          <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3" data-testid="publish-verify-hint">
            <p className="text-sm text-amber-300 leading-relaxed">Completa los datos de tu empresa en <Link to="/mi-perfil" className="underline">tu perfil</Link> para publicar con el sello «Verificada» y generar más confianza.</p>
          </div>
        )}
        <form onSubmit={submit} className="space-y-6 mb-16" data-testid="publish-form">
          <section className="sc-card p-6 md:p-8 space-y-5">
            <div>
              <label className={lbl}>Título de la oferta</label>
              <input data-testid="publish-title" className={inp} value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Técnico/a de iluminación · GrandMA3" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Categoría</label>
                <select data-testid="publish-category" className={inp} value={form.category} onChange={(e) => set('category', e.target.value)}>
                  <option value="">Selecciona…</option>
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Tipo</label>
                <select data-testid="publish-type" className={inp} value={form.type} onChange={(e) => set('type', e.target.value)}>
                  {JOB_TYPES.map((jt) => <option key={jt.id} value={jt.id}>{jt.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Ubicación</label>
                <input data-testid="publish-location" className={inp} value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Barcelona, Catalunya" />
              </div>
              <div>
                <label className={lbl}>Fecha límite para candidaturas</label>
                <input data-testid="publish-deadline" type="date" className={inp} value={form.deadline} onChange={(e) => set('deadline', e.target.value)} />
              </div>
            </div>
          </section>

          <section className="sc-card p-6 md:p-8" data-testid="publish-schedule-section">
            <div className="flex items-center gap-2 mb-5">
              <CalendarDays size={15} className="text-amber-500" />
              <h3 className="sc-mono-eyebrow text-amber-500">Fechas y horario</h3>
            </div>
            <div className="flex flex-wrap gap-2 mb-6">
              <button type="button" data-testid="duration-un-dia" onClick={() => setDurationType('un-dia')} className={chip(durationType === 'un-dia')}>Un solo día</button>
              <button type="button" data-testid="duration-varios-dias" onClick={() => setDurationType('varios-dias')} className={chip(durationType === 'varios-dias')}>Varios días / larga duración</button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
              <div className="sc-card bg-[#0B0C0E] p-2 inline-block mx-auto lg:mx-0" data-testid="publish-calendar">
                {durationType === 'un-dia' ? (
                  <Calendar mode="single" selected={singleDate} onSelect={setSingleDate} locale={es} classNames={CAL_CLASSES} />
                ) : (
                  <Calendar mode="range" selected={range} onSelect={setRange} locale={es} numberOfMonths={1} classNames={CAL_CLASSES} />
                )}
              </div>
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Hora inicio</label>
                    <input data-testid="publish-start-time" type="time" className={inp} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                  </div>
                  <div>
                    <label className={lbl}>Hora fin</label>
                    <input data-testid="publish-end-time" type="time" className={inp} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                  </div>
                </div>
                <div className="sc-card bg-[#0B0C0E] p-4 space-y-2" data-testid="publish-hours-summary">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 flex items-center gap-2"><CalendarDays size={13} /> Días</span>
                    <span className="font-mono text-white" data-testid="calc-days">{calc.days}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 flex items-center gap-2"><Clock size={13} /> Horas / día</span>
                    <span className="font-mono text-white" data-testid="calc-hours-day">{calc.hoursPerDay} h</span>
                  </div>
                  <div className="flex items-center justify-between text-sm pt-2 border-t border-[#2A2F3D]">
                    <span className="text-amber-500 flex items-center gap-2 font-semibold"><Sigma size={13} /> Total horas</span>
                    <span className="font-mono text-amber-500 text-lg font-semibold" data-testid="calc-total">{calc.total} h</span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-snug pt-1" data-testid="calc-schedule-label">
                    {scheduleLabel || 'Selecciona una fecha en el calendario para calcular las horas automáticamente.'}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="sc-card p-6 md:p-8 space-y-5">
            <div>
              <label className={lbl}>Retribución</label>
              <input data-testid="publish-comp" className={inp} value={form.compensation} onChange={(e) => set('compensation', e.target.value)} placeholder="180 €/día + IVA" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <UtensilsCrossed size={13} className="text-emerald-500" />
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Dietas</label>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {Object.entries(PER_DIEM_LABELS).map(([id, label]) => (
                  <button key={id} type="button" data-testid={`perdiem-${id}`} onClick={() => set('perDiem', id)} className={chip(form.perDiem === id)}>{label}</button>
                ))}
              </div>
              {form.perDiem !== 'none' && (
                <input data-testid="publish-perdiem-detail" className={inp} value={form.perDiemDetail} onChange={(e) => set('perDiemDetail', e.target.value)} placeholder="Detalle opcional · ej. 25 €/día, catering en montaje, hotel incluido" />
              )}
            </div>
            <div>
              <label className={lbl}>Habilidades técnicas (separadas por coma)</label>
              <input data-testid="publish-skills" className={inp} value={form.skills} onChange={(e) => set('skills', e.target.value)} placeholder="GrandMA3, DMX, ETC Ion" />
            </div>
            <div>
              <label className={lbl}>Descripción</label>
              <textarea data-testid="publish-description" rows={5} className={inp + ' resize-none'} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Detalla contexto, expectativas y equipo técnico disponible…" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-[11px] text-slate-500 leading-snug">Al publicar aceptas los <Link to="/legal#terminos" className="text-amber-500 hover:underline">términos de uso</Link>: eres responsable del contenido y de las condiciones que ofreces.</p>
              <button data-testid="publish-submit" disabled={saving} className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors">
                <Send size={16} />
                {saving ? (editId ? 'Guardando…' : 'Publicando…') : (editId ? 'Guardar cambios' : 'Publicar oferta')}
              </button>
            </div>
          </section>
          <p className="text-[11px] text-slate-500 font-mono uppercase tracking-widest text-center">{editId ? 'Los cambios se reflejan al instante en el tablón.' : 'Tu oferta se publica al instante en el tablón.'}</p>
        </form>
      </div>
      <Footer />
    </div>
  );
};

const PublicarOfertaPage = () => (
  <AuthGate><Inner /></AuthGate>
);

export default PublicarOfertaPage;
